import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Constrants from "../constrants/constrant";
import { useToast } from "../context/ToastProvider";

function ResetPassword() {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const [searchParams] = useSearchParams();

    const [email, setEmail] = useState("");
    const [token, setToken] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const emailParam = searchParams.get("email");
        const tokenParam = searchParams.get("token");

        if (emailParam) setEmail(emailParam);
        if (tokenParam) setToken(tokenParam);

        if (!emailParam || !tokenParam) {
            addToast("Invalid reset link. Please request a new one.", "error");
            navigate("/forgot-password");
        }
    }, [searchParams, navigate, addToast]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== passwordConfirmation) {
            addToast("Passwords do not match", "error");
            return;
        }

        if (password.length < 8) {
            addToast("Password must be at least 8 characters long", "error");
            return;
        }

        setLoading(true);

        try {
            const res = await Constrants.post("/reset-password", {
                email,
                token,
                password,
                password_confirmation: passwordConfirmation,
            });

            addToast(res.data.message, "success", 5000);
            setTimeout(() => {
                navigate("/login");
            }, 2000);
        } catch (err) {
            console.error("Reset password error:", err);

            if (err.response?.data?.message) {
                addToast(err.response.data.message, "error");
            } else if (err.response?.data?.errors) {
                const errors = Object.values(err.response.data.errors).flat();
                addToast(errors.join(", "), "error");
            } else {
                addToast("Failed to reset password. Please try again.", "error");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="main-content">
            <div className="auth-container">
                <div className="auth-card active">
                    <div className="auth-header">
                        <div className="auth-logo">
                            <div className="logo-icon" style={{ width: "48px", height: "48px" }}>
                                <svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 0 24 24" width="48px" fill="white">
                                    <path d="M0 0h24v24H0V0z" fill="none" /><path d="M17.81 4.47c-.08 0-.16-.02-.23-.06C15.66 3.42 14 3 12.01 3c-1.98 0-3.86.47-5.57 1.41-.24.13-.54.04-.68-.2-.13-.24-.04-.55.2-.68C7.82 2.52 9.86 2 12.01 2c2.13 0 3.99.47 6.03 1.52.25.13.34.43.21.67-.09.18-.26.28-.44.28zM3.5 9.72c-.1 0-.2-.03-.29-.09-.23-.16-.28-.47-.12-.7.99-1.4 2.25-2.5 3.75-3.27C9.98 4.04 14 4.03 17.15 5.65c1.5.77 2.76 1.86 3.75 3.25.16.22.11.54-.12.7-.23.16-.54.11-.7-.12-.9-1.26-2.04-2.25-3.39-2.94-2.87-1.47-6.54-1.47-9.4.01-1.36.7-2.5 1.7-3.4 2.96-.08.14-.23.21-.39.21zm6.25 12.07c-.13 0-.26-.05-.35-.15-.87-.87-1.34-1.43-2.01-2.64-.69-1.23-1.05-2.73-1.05-4.34 0-2.97 2.54-5.39 5.66-5.39s5.66 2.42 5.66 5.39c0 .28-.22.5-.5.5s-.5-.22-.5-.5c0-2.42-2.09-4.39-4.66-4.39s-4.66 1.97-4.66 4.39c0 1.44.32 2.77.93 3.85.64 1.15 1.08 1.64 1.85 2.42.19.2.19.51 0 .71-.11.10-.24.15-.37.15zm7.17-1.85c-1.19 0-2.24-.3-3.1-.89-1.49-1.01-2.38-2.65-2.38-4.39 0-.28.22-.5.5-.5s.5.22.5.5c0 1.41.72 2.74 1.94 3.56.71.48 1.54.71 2.54.71.24 0 .64-.03 1.04-.1.27-.05.53.13.58.41.05.27-.13.53-.41.58-.57.11-1.07.12-1.21.12zM14.91 22c-.04 0-.09-.01-.13-.02-1.59-.44-2.63-1.03-3.72-2.10-1.4-1.39-2.17-3.24-2.17-5.22 0-1.62 1.38-2.94 3.08-2.94s3.08 1.32 3.08 2.94c0 1.07.93 1.94 2.08 1.94s2.08-.87 2.08-1.94c0-3.77-3.25-6.83-7.25-6.83-2.84 0-5.44 1.58-6.61 4.03-.39.81-.59 1.76-.59 2.8 0 .78.07 2.01.67 3.61.1.26-.03.55-.29.64-.26.10-.55-.04-.64-.29-.49-1.31-.73-2.61-.73-3.96 0-1.2.23-2.29.68-3.24 1.33-2.79 4.28-4.6 7.51-4.6 4.55 0 8.25 3.51 8.25 7.83 0 1.62-1.38 2.94-3.08 2.94s-3.08-1.32-3.08-2.94c0-1.07-.93-1.94-2.08-1.94s-2.08.87-2.08 1.94c0 1.71.66 3.31 1.87 4.51.95.94 1.86 1.46 3.27 1.85.27.07.42.35.35.61-.05.23-.26.38-.47.38z" />
                                </svg>
                            </div>
                        </div>
                        <h1 className="auth-title">Reset Password</h1>
                        <p className="auth-subtitle">Enter your new password</p>
                    </div>

                    <form className="auth-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="email" className="form-label">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                className="form-input"
                                value={email}
                                readOnly
                                disabled
                                style={{ background: "#f5f5f5", cursor: "not-allowed" }}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password" className="form-label">New Password</label>
                            <input
                                type="password"
                                id="password"
                                className="form-input"
                                placeholder="Enter new password (min 8 characters)"
                                required
                                minLength="8"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="passwordConfirmation" className="form-label">Confirm Password</label>
                            <input
                                type="password"
                                id="passwordConfirmation"
                                className="form-input"
                                placeholder="Re-enter your password"
                                required
                                minLength="8"
                                value={passwordConfirmation}
                                onChange={(e) => setPasswordConfirmation(e.target.value)}
                            />
                        </div>

                        <button type="submit" className="auth-btn primary" disabled={loading}>
                            {loading ? "Resetting..." : "Reset Password"}
                        </button>

                        <div className="auth-footer" style={{ marginTop: "20px" }}>
                            Remember your password? <Link to="/login" className="link-text">Sign in</Link>
                        </div>
                    </form>
                </div>
            </div>

            <section className="section">
                <div className="section-header">
                    <h2 className="section-title">Password Requirements</h2>
                </div>
                <div className="categories-grid">
                    <div className="category-card">
                        <div className="category-icon" style={{ background: "linear-gradient(45deg, #667eea, #764ba2)" }}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="white">
                                <path d="M0 0h24v24H0V0z" fill="none" /><path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z" />
                            </svg>
                        </div>
                        <div className="category-name">At least 8 characters</div>
                    </div>

                    <div className="category-card">
                        <div className="category-icon" style={{ background: "linear-gradient(45deg, #4caf50, #8bc34a)" }}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="white">
                                <path d="M0 0h24v24H0V0z" fill="none" /><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
                            </svg>
                        </div>
                        <div className="category-name">Passwords must match</div>
                    </div>

                    <div className="category-card">
                        <div className="category-icon" style={{ background: "linear-gradient(45deg, #f093fb, #f5576c)" }}>
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="white">
                                <path d="M0 0h24v24H0V0z" fill="none" /><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                            </svg>
                        </div>
                        <div className="category-name">Use a strong password</div>
                    </div>
                </div>
            </section>
        </main>
    );
}

export default ResetPassword;
